import { TxOption } from "./types";
import { mapProcedureLabelToCanonicalVisual } from "../../../utils/toothVisualMapping";

const glyph = (code: number): string => String.fromCharCode(code);

export const ColorsDictionary = {
  c01: "#E53935",
  c02: "#D81B60",
  c03: "#8E24AA",
  c04: "#5E35B1",
  c05: "#3949AB",
  c06: "#1E88E5",
  c07: "#039BE5",
  c08: "#00ACC1",
  c09: "#00897B",
  c10: "#2962FF",
  c11: "#43A047",
  c12: "#7CB342",
  c13: "#C0CA33",
  c14: "#C0CA33",
  c15: "rgba(202, 139, 51, 1)",
  c16: "rgba(57, 209, 93, 1)",
  c17: "rgba(1, 207, 131, 1)",
  c18: "#6D4C41",
};

const BASE_TX_OPTIONS: TxOption[] = [
  { label: "missing", masterLabel: "Edentulism partial", icon: glyph(0xe80c), color: ColorsDictionary.c01, type: "state" },
  { label: "extraction", masterLabel: "Simple extraction", icon: glyph(0xe800), color: ColorsDictionary.c01, type: "treatment" },
  { label: "filling", masterLabel: "Direct filling composite", icon: glyph(0xe802), color: ColorsDictionary.c02, type: "both" },
  { label: "pulpotomy", masterLabel: "Pulp capping direct", icon: glyph(0xe801), color: ColorsDictionary.c03, type: "treatment" },
  { label: "caries", masterLabel: "Dental caries", icon: glyph(0xe822), color: ColorsDictionary.c03, type: "state" },
  { label: "rCT", masterLabel: "Root canal therapy anterior", icon: glyph(0xe803), color: ColorsDictionary.c04, type: "both" },
  { label: "fractured", masterLabel: "Tooth fracture – enamel", icon: glyph(0xe824), color: ColorsDictionary.c05, type: "state" },
  { label: "re-RCT", masterLabel: "Root canal therapy posterior", icon: glyph(0xe804), color: ColorsDictionary.c05, type: "treatment" },
  { label: "mobility", masterLabel: "Periodontitis moderate", icon: glyph(0xe826), color: ColorsDictionary.c10, type: "state" },
  { label: "ortho", masterLabel: "Orthodontic consultation", icon: glyph(0xe80f), color: ColorsDictionary.c10, type: "treatment" },
  { label: "recession", masterLabel: "Gingival hyperplasia", icon: glyph(0xe827), color: ColorsDictionary.c06, type: "state" },
  { label: "whitening", masterLabel: "Dental prophylaxis", icon: glyph(0xe806), color: ColorsDictionary.c06, type: "treatment" },
  { label: "rroot", masterLabel: "Retained root fragment", icon: glyph(0xe82c), color: ColorsDictionary.c07, type: "state" },
  { label: "clean", masterLabel: "Scaling and root planing", icon: glyph(0xe805), color: ColorsDictionary.c07, type: "treatment" },
  { label: "implant", masterLabel: "Implant placement stage 1", icon: glyph(0xe807), color: ColorsDictionary.c08, type: "both" },
  { label: "rprimary", masterLabel: "Supernumerary tooth", icon: glyph(0xe829), color: ColorsDictionary.c09, type: "state" },
  { label: "surgery", masterLabel: "Gingival flap surgery", icon: glyph(0xe808), color: ColorsDictionary.c09, type: "treatment" },
  { label: "malposition", masterLabel: "Malocclusion – crowding", icon: glyph(0xe82a), color: ColorsDictionary.c09, type: "state" },
  { label: "crown", masterLabel: "Crown placement", icon: glyph(0xe809), color: ColorsDictionary.c11, type: "both" },
  { label: "veneer", masterLabel: "Crown preparation", icon: glyph(0xe80e), color: ColorsDictionary.c12, type: "both" },
  { label: "overlay", masterLabel: "Light cure restoration", icon: glyph(0xe80a), color: ColorsDictionary.c16, type: "both" },
  { label: "impacted", masterLabel: "Impacted tooth", icon: glyph(0xe82b), color: ColorsDictionary.c17, type: "state" },
  { label: "temporary", masterLabel: "Light cure restoration", icon: glyph(0xe80b), color: ColorsDictionary.c17, type: "treatment" },
  { label: "bridge", masterLabel: "Bridge placement", icon: glyph(0xe80d), color: ColorsDictionary.c13, type: "both" },
  { label: "abutment", masterLabel: "Post and core buildup", icon: glyph(0xe821), color: ColorsDictionary.c14, type: "both" },
  { label: "pontic", masterLabel: "Bridge preparation", icon: glyph(0xe820), color: ColorsDictionary.c15, type: "both" },
  { label: "other", icon: glyph(0xe80c), color: ColorsDictionary.c18, type: "both" },
];

let txOptionsCache: TxOption[] = [...BASE_TX_OPTIONS];

const normalize = (value: string): string =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeKey = (value: string): string =>
  normalize(value).replace(/[^a-z0-9]+/g, "");

const pickMasterLabel = (row: unknown): string => {
  if (!row || typeof row !== "object") return "";
  const rec = row as Record<string, unknown>;
  const raw =
    rec.description ??
    rec.diagnosis ??
    rec.display_name ??
    rec.name ??
    rec.procedure_name ??
    rec.label;
  return String(raw || "").trim();
};

const ALIAS_KEYWORDS: Record<string, string[]> = {
  missing: ["missing", "edentulous"],
  extraction: ["extraction", "extract"],
  filling: ["filling", "composite", "restoration", "restorative"],
  pulpotomy: ["pulpotomy"],
  caries: ["caries", "decay", "cavit"],
  rCT: ["rct", "rootcanal", "rootcanaltreatment", "endodontic", "endo"],
  fractured: ["fracture", "broken"],
  "re-RCT": ["rerct", "re-endo", "retreatment"],
  mobility: ["mobility", "mobiletooth"],
  ortho: ["ortho", "orthodontic", "braces"],
  recession: ["recession"],
  whitening: ["whitening", "bleach"],
  clean: ["scaling", "clean", "prophy", "polish"],
  implant: ["implant"],
  surgery: ["surgery", "surgical"],
  crown: ["crown", "cap"],
  veneer: ["veneer"],
  bridge: ["bridge", "fpd", "fixedpartialdenture"],
  abutment: ["abutment"],
  pontic: ["pontic"],
};

const findAliasBase = (label: string): TxOption | null => {
  const canonical = mapProcedureLabelToCanonicalVisual(label);
  if (canonical === "missing") return BASE_TX_OPTIONS.find((x) => x.label === "missing") || null;
  if (canonical === "filling") return BASE_TX_OPTIONS.find((x) => x.label === "filling") || null;
  if (canonical === "rct_open" || canonical === "rct_done") return BASE_TX_OPTIONS.find((x) => x.label === "rCT") || null;
  if (canonical === "rct_cc") return BASE_TX_OPTIONS.find((x) => x.label === "overlay") || null;
  if (canonical === "rct_crown" || canonical === "crown") return BASE_TX_OPTIONS.find((x) => x.label === "crown") || null;
  if (canonical === "implant") return BASE_TX_OPTIONS.find((x) => x.label === "implant") || null;

  const labelKey = normalizeKey(label);
  if (!labelKey) return null;
  for (const base of BASE_TX_OPTIONS) {
    const aliases = ALIAS_KEYWORDS[base.label] || [base.label];
    if (aliases.some((token) => labelKey.includes(normalizeKey(token)))) {
      return base;
    }
  }
  return null;
}

type MasterTxInput = {
  diagnosisRows?: unknown[];
  procedureRows?: unknown[];
};

export function setTxOptionsFromMaster(input: MasterTxInput): TxOption[] {
  const diagnosisRows = Array.isArray(input?.diagnosisRows) ? input.diagnosisRows : [];
  const procedureRows = Array.isArray(input?.procedureRows) ? input.procedureRows : [];
  const seen = new Set<string>();
  const next: TxOption[] = [];

  const pushOption = (candidate: TxOption) => {
    const key = normalize(candidate.label);
    if (!key || seen.has(key)) return;
    seen.add(key);
    next.push(candidate);
  };

  // Keep known wheel options first for consistent UX.
  BASE_TX_OPTIONS.forEach((opt) => pushOption(opt));

  const appendMasterRows = (rows: unknown[], forcedType: TxOption["type"]) => {
    for (const row of rows) {
      const label = pickMasterLabel(row);
      if (!label) continue;
      const alias = findAliasBase(label);
      if (alias) {
        // Preserve wheel icon/color semantics while using master label text.
        pushOption({
          label,
          icon: alias.icon,
          color: alias.color,
          type: alias.type,
        });
      } else {
        pushOption({
          label,
          icon: labelToIcon("other"),
          color: ColorsDictionary.c18,
          type: forcedType,
        });
      }
    }
  };

  appendMasterRows(diagnosisRows, "state");
  appendMasterRows(procedureRows, "treatment");
  txOptionsCache = next;
  return txOptionsCache;
}

export const txOptions = BASE_TX_OPTIONS;

export function getTxOptions(): TxOption[] {
  return txOptionsCache;
}

export function labelToColor(label: string): string {
  const option = txOptionsCache.find((x) => x.label === label || x.masterLabel === label);
  return option ? option.color : ColorsDictionary.c18;
}

export function labelToIcon(label: string): string {
  const option = txOptionsCache.find((x) => x.label === label || x.masterLabel === label);
  return option ? option.icon : glyph(0xe80c);
}

export function resolveTxOptionVisual(label: string): { icon: string; color: string } {
  const exact = txOptionsCache.find((x) => normalize(x.label) === normalize(label) || normalize(x.masterLabel || "") === normalize(label));
  if (exact) return { icon: exact.icon, color: exact.color };

  const alias = findAliasBase(label);
  if (alias) return { icon: alias.icon, color: alias.color };

  return { icon: glyph(0xe80c), color: ColorsDictionary.c18 };
}
